/**
 * HotkeyCommandPalette — the Ctrl/⌘+K "run anything" palette (CLAUDE.md standing
 * rule 14, §18.6, ADR-0013 Phase 3). A fuzzy search over the SAME live registry
 * the `?` sheet shows: every on-screen action AND every nav chord is a registered
 * binding, so one list gives "jump to any action or form". Enter (or click) runs
 * the picked binding via `runHotkeyById`; ↑/↓ move; Esc (or Ctrl+K again) closes.
 *
 * Disabled bindings are omitted (they can't run). Like the cheat sheet it is an
 * `aria-modal` dialog, so the global dispatcher yields the keyboard to it and no
 * form shortcut fires underneath while it is open.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeyList, runHotkeyById } from './hotkeyStore';
import { useNavPalette } from './navPaletteStore';
import { formatCombo } from './keyCombo';
import { fuzzyFilter } from '../../utils/fuzzyMatch';
import './hotkeys.css';

/** One row in the palette — an on-screen action / chord (from the hotkey
 *  registry) OR a "jump to a form" nav entry (from the nav-palette source). */
interface Cmd { key: string; group: string; label: string; combo: string; run: () => void }

export function HotkeyCommandPalette({ onClose }: { onClose: () => void }) {
  const items = useHotkeyList((s) => s.items);
  const navItems = useNavPalette((s) => s.items);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Merge the two sources into one flat command list: enabled hotkey bindings
  // first (they carry the key hint), then every navigable form the user can
  // reach. A form whose label a binding already offers (e.g. a `g`-chord target)
  // is skipped, so the same destination isn't listed twice.
  const runnable = useMemo<Cmd[]>(() => {
    const out: Cmd[] = [];
    const seen = new Set<string>();
    for (const it of items) {
      if (it.disabled) continue;
      seen.add(it.label.toLowerCase());
      out.push({ key: `h${it.id}`, group: it.group, label: it.label, combo: it.combo, run: () => runHotkeyById(it.id) });
    }
    for (const n of navItems) {
      if (seen.has(n.label.toLowerCase())) continue;
      out.push({ key: `n:${n.id}`, group: n.group, label: n.label, combo: '', run: n.run });
    }
    return out;
  }, [items, navItems]);

  const results = useMemo(
    () => fuzzyFilter(runnable, query, (it) => `${it.group} ${it.label} ${it.combo}`),
    [runnable, query],
  );

  // Clamp the highlight into range as results shrink/grow.
  const activeIdx = results.length ? Math.min(active, results.length - 1) : 0;

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIdx, results]);

  const runAt = (i: number) => {
    const it = results[i];
    if (!it) return;
    onClose();
    it.run();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (results.length ? (Math.min(a, results.length - 1) + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (results.length ? (Math.min(a, results.length - 1) - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runAt(activeIdx);
    } else if (e.key === 'Escape' || (e.key.toLowerCase() === 'k' && (e.ctrlKey || e.metaKey))) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  return (
    <div className="hk-overlay hk-cmd-overlay" role="presentation" onClick={onClose}>
      <div
        className="hk-cmd"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          className="hk-cmd-input"
          type="text"
          placeholder="Run a command or jump to a form…   ↑↓ move · Enter run · Esc close"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label="Command search"
        />
        <ul className="hk-cmd-list" ref={listRef}>
          {results.length === 0 && <li className="hk-cmd-empty">No matching commands.</li>}
          {results.map((it, i) => (
            <li
              key={it.key}
              data-idx={i}
              className={`hk-cmd-row${i === activeIdx ? ' hk-cmd-active' : ''}`}
              onMouseMove={() => setActive(i)}
              onClick={() => runAt(i)}
            >
              <span className="hk-cmd-group">{it.group}</span>
              <span className="hk-cmd-label">{it.label}</span>
              {it.combo && <kbd className="hk-kbd">{formatCombo(it.combo)}</kbd>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
